import React from 'react';
import { Box, Card, Typography, Grid, useTheme, CardContent } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BiotechIcon from '@mui/icons-material/Biotech';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import DiagnosisIcon from '@mui/icons-material/Assignment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MedicationIcon from '@mui/icons-material/Medication';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import CategoryIcon from '@mui/icons-material/Category';

// Animations
const glowAnimation = keyframes`
  0% { box-shadow: 0 4px 15px rgba(25, 118, 210, 0.1); }
  50% { box-shadow: 0 8px 25px rgba(25, 118, 210, 0.2); }
  100% { box-shadow: 0 4px 15px rgba(25, 118, 210, 0.1); }
`;

// Styled Components
const CodeCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(25, 118, 210, 0.1)',
  borderRadius: '16px',
  padding: theme.spacing(3),
  height: '100%',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${glowAnimation} 4s infinite`,
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 12px 40px rgba(25, 118, 210, 0.25)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)',
    border: '1px solid rgba(25, 118, 210, 0.3)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
  borderRadius: '12px',
  width: 60,
  height: 60,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  color: 'white',
  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
}));



const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const codePurposes = [
  { 
    key: 'clinical-data', 
    label: 'Clinical Data', 
    icon: BiotechIcon,
    description: 'Laboratory and clinical data codes',
    color: '#2196f3'
  },
  { 
    key: 'clinical-terms', 
    label: 'Clinical Terms', 
    icon: MedicalServicesIcon,
    description: 'Medical terminology and clinical terms',
    color: '#ff9800'
  },
  { 
    key: 'diagnosis-codes', 
    label: 'Diagnosis Codes', 
    icon: DiagnosisIcon,
    description: 'ICD-10 and other diagnosis codes',
    color: '#e91e63'
  },
  { 
    key: 'local-use', 
    label: 'Local Use', 
    icon: LocationOnIcon,
    description: 'Facility-specific and local use codes',
    color: '#9c27b0'
  },
  { 
    key: 'medication-coding', 
    label: 'Medication Coding', 
    icon: MedicationIcon,
    description: 'Drug codes and medication classifications',
    color: '#f44336'
  },
  { 
    key: 'immunization-codes', 
    label: 'Immunization Codes', 
    icon: VaccinesIcon,
    description: 'Vaccine and immunization codes',
    color: '#00bcd4'
  },
  { 
    key: 'taxonomy-codes', 
    label: 'Taxonomy Codes', 
    icon: CategoryIcon,
    description: 'Healthcare provider taxonomy codes',
    color: '#4caf50'
  },
];

const Codes = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
            Codes
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Select a code category to manage its code types and classifications
          </Typography>
        </CardContent>
      </HeaderCard>

      <Grid container spacing={4}>
        {codePurposes.map((purpose, index) => {
          const IconComponent = purpose.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={purpose.key}>
              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <CodeCard
                  onClick={() => navigate(`/admin/codes/${purpose.key}`)}
                  elevation={0}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <IconWrapper>
                      <IconComponent sx={{ fontSize: 30 }} />
                    </IconWrapper>
                    
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        color: theme.palette.text.primary,
                        fontSize: '1.25rem'
                      }}
                    >
                      {purpose.label}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                        fontSize: '0.875rem'
                      }}
                    >
                      {purpose.description}
                    </Typography>
                  </Box>
                </CodeCard>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default Codes; 